import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOnceAsks } from '../Shell/ApplicationsBridge';
import './AskOnce.css';

// Under a reply that started applications (queue_job): the questions only
// the person can answer that are holding them up (decision A, founder,
// 2026-09-28). Each is one record, kept at the top of Applications; this is
// that page's own card and save (ApplicationsBridge.js), so answering here
// answers it there, and one answered there leaves this card too. Drawn as
// the design canvas draws it: "Asked once", who is waiting, the choices.

/** "Roku is waiting", "Roku and Datadog are waiting", "Roku, Datadog and 2 more are waiting". */
export function waitingLine(ids = [], companies = {}) {
  const names = [...new Set(ids.map(id => companies[String(id)]).filter(Boolean))];
  if (!names.length) return '';
  const who = names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  return `${who} ${names.length === 1 ? 'is' : 'are'} waiting`;
}

export default function AskOnce({ jobs = [] }) {
  const bridge = useOnceAsks();
  const [closed, setClosed] = useState(false);
  // Once a question was shown, the card ends on a word instead of vanishing.
  const shown = useRef(false);
  // The most it has held: one question needs no count, no bar and no Skip.
  const most = useRef(0);
  const ids = useMemo(() => new Set(jobs.map(String)), [jobs]);
  const companies = bridge?.companies;
  const items = useMemo(() => (bridge?.items || [])
    .filter(i => (i.once?.jobs || []).some(id => ids.has(String(id)))), [bridge, ids]);

  // A job queued a moment ago is not on the page's list yet: it is asked for
  // once (the page merges asks that overlap), and read from there on.
  const missing = Boolean(bridge) && [...ids].some(id => !(id in (companies || {})));
  const refresh = bridge?.refresh;
  useEffect(() => { if (missing && refresh) refresh({ read: 'unread' }); }, [missing, refresh]);

  if (!bridge || closed) return null;
  if (items.length) shown.current = true;
  most.current = Math.max(most.current, items.length);
  if (!items.length) {
    // Gone because it was answered, here or at the top; an application
    // taken off the list meanwhile says nothing.
    const stillListed = [...ids].some(id => id in (companies || {}));
    return shown.current && stillListed ? (
      <div className="ag-cc-status is-done" role="status">
        <span className="material-symbols-rounded" aria-hidden="true">check</span>
        Answered
      </div>
    ) : null;
  }
  const { Flow } = bridge;
  const head = (item) => (
    <div className="ag-ask-head">
      <span className="ag-ask-label">
        <span className="material-symbols-rounded" aria-hidden="true">person</span>
        Asked once
      </span>
      <span className="ag-ask-who">{waitingLine(item.once?.jobs, companies)}</span>
    </div>
  );
  return (
    <div className={`ag-ask${most.current === 1 ? ' is-one' : ''}`}>
      <Flow items={items} busy={bridge.busy} onSave={bridge.save} onDone={() => setClosed(true)} doneText="" head={head} embedded follow />
    </div>
  );
}
